import { filterKitchenOrderItems, orderRequiresKitchen } from "./orderModel";
import { buildCustomerBillHtml } from "./customerBill";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "./runtimeEnvironment";

const escapeHtml = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const formatKitchenDateTime = (value) => {
  if (!value) {
    return "Sin horario";
  }

  try {
    return new Date(value).toLocaleString("es-AR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch (error) {
    return String(value);
  }
};

const formatOrderId = (value) => {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return "Sin codigo";
  }

  return rawValue.length > 18 ? rawValue.slice(-18) : rawValue;
};

export const formatPreparationNumber = (value) => {
  const parsedValue = Number(value || 0);

  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return "";
  }

  return String(Math.floor(parsedValue)).padStart(3, "0");
};

const resolveKitchenTicketPaperConfig = (value) => {
  if (value === "58mm") {
    return {
      pageWidth: "58mm",
      pageMargin: "3mm",
      bodyWidth: "52mm",
      shellPadding: "11px 10px 12px",
      shellRadius: "16px",
      brandWidth: "84px",
      brandHeight: "24px",
      brandFontSize: "15px",
      ticketGap: "10px",
      kickerSize: "9px",
      titleSize: "15px",
      codeSize: "32px",
      headlineSize: "24px",
      branchSize: "10px",
      metaLabelSize: "10px",
      metaValueSize: "10px",
      sectionTitleSize: "10px",
      itemTitleSize: "10.5px",
      detailSize: "9px",
      totalSize: "12px",
      footerSize: "8.5px",
      noteSize: "9px",
      gridGap: "8px",
    };
  }

  return {
    pageWidth: "80mm",
    pageMargin: "4mm",
    bodyWidth: "72mm",
    shellPadding: "14px 13px 15px",
    shellRadius: "18px",
    brandWidth: "96px",
    brandHeight: "28px",
    brandFontSize: "17px",
    ticketGap: "12px",
    kickerSize: "10px",
    titleSize: "18px",
    codeSize: "40px",
    headlineSize: "28px",
    branchSize: "11px",
    metaLabelSize: "11px",
    metaValueSize: "11px",
    sectionTitleSize: "11px",
    itemTitleSize: "12px",
    detailSize: "10px",
    totalSize: "14px",
    footerSize: "9.5px",
    noteSize: "10px",
    gridGap: "9px",
  };
};

const resolveTicketItemName = (item) =>
  escapeHtml(
    item?.productName || item?.sourceProductName || item?.name || item?.nombre || "Producto"
  );

const resolveTicketItemNotes = (item) => {
  const notes = [];

  if (Array.isArray(item?.selectionDetails)) {
    item.selectionDetails
      .map((detail) => String(detail || "").trim())
      .filter(Boolean)
      .forEach((detail) => notes.push(detail));
  }

  const freeNotes = String(item?.notes || "").trim();
  if (freeNotes) {
    notes.push(freeNotes);
  }

  return notes;
};

const buildTicketItemNotesMarkup = (item) => {
  const notes = resolveTicketItemNotes(item);

  if (!notes.length) {
    return "";
  }

  return `<div class="ticket-item-details">${notes
    .map((detail) => escapeHtml(detail))
    .join(" | ")}</div>`;
};

const resolveCustomerTicketPaymentLabel = (order) => {
  const explicitLabel = [
    order?.paymentMethodLabel,
    order?.paymentLabel,
    order?.walletProviderLabel,
    order?.menuWalletProviderLabel,
    order?.selectedWalletProviderLabel,
  ]
    .map((value) => String(value || "").trim())
    .find(Boolean);

  if (explicitLabel) {
    return explicitLabel;
  }

  const normalizedMethod = String(order?.checkoutMethod || order?.paymentMethod || "")
    .trim()
    .toLowerCase();

  if (
    normalizedMethod === "counter" ||
    normalizedMethod === "cash" ||
    normalizedMethod === "manual_cash" ||
    normalizedMethod === "caja"
  ) {
    return "Caja";
  }

  if (normalizedMethod === "qr" || normalizedMethod === "kiosk_qr") {
    return "QR";
  }

  if (normalizedMethod === "deep_link") {
    return "Pago digital";
  }

  return "";
};

const resolveKitchenChannelLabel = (order) => {
  const explicitLabel = [order?.canal, order?.channelLabel, order?.sourceChannelLabel]
    .map((value) => String(value || "").trim())
    .find(Boolean);

  if (explicitLabel) {
    return explicitLabel;
  }

  const normalizedChannel = String(order?.sourceChannel || "")
    .trim()
    .toLowerCase();
  if (normalizedChannel === "menu_qr") {
    return "Menu QR";
  }
  if (normalizedChannel === "kiosk") {
    return "Totem";
  }

  const serviceType = String(order?.serviceType || "")
    .trim()
    .toLowerCase();
  if (serviceType === "takeaway") {
    return "Retiro";
  }

  return "Salon";
};

const resolveKitchenHeadline = (order) => {
  const mesaLabel = String(order?.mesa || order?.tableLabel || order?.displayName || "").trim();
  if (mesaLabel) {
    return mesaLabel;
  }

  const preparationCode = formatPreparationNumber(order?.preparationNumber);
  if (preparationCode) {
    return `Retiro #${preparationCode}`;
  }

  const serviceType = String(order?.serviceType || "")
    .trim()
    .toLowerCase();

  if (serviceType === "takeaway") {
    return "Retiro";
  }

  if (serviceType === "dine_in") {
    return "Salon";
  }

  return "Pedido";
};

const resolveKitchenSecondaryLabel = (order) => {
  const parts = [];
  const preparationCode = formatPreparationNumber(order?.preparationNumber);
  const customerName = String(order?.customerName || "").trim();

  if (preparationCode) {
    parts.push(`Codigo #${preparationCode}`);
  }

  if (customerName) {
    parts.push(customerName);
  }

  return parts.join(" | ");
};

const buildKitchenItemMarkup = (item, index) => {
  const quantity = Math.max(1, Number(item?.quantity || 1));
  const productName = resolveTicketItemName(item);

  return `
    <li class="ticket-item">
      <div class="ticket-item-main">
        <div class="ticket-item-title">${index + 1}. ${quantity} x ${productName}</div>
        ${buildTicketItemNotesMarkup(item)}
      </div>
    </li>
  `;
};

const buildKitchenTicketHtml = ({ restaurantName, branchName, order, paperWidth }) => {
  const headerTitle = escapeHtml(restaurantName || "MozzoApp");
  const paperConfig = resolveKitchenTicketPaperConfig(paperWidth);
  const branchLabel =
    branchName && branchName !== restaurantName
      ? `<div class="ticket-branch">${escapeHtml(branchName)}</div>`
      : "";
  const customerName = String(order?.customerName || "").trim();
  const notes = String(order?.notes || "").trim();
  const items = filterKitchenOrderItems(order?.items);
  const headline = escapeHtml(resolveKitchenHeadline(order));
  const secondaryLabel = resolveKitchenSecondaryLabel(order);
  const channelLabel = resolveKitchenChannelLabel(order);
  const orderedAtLabel = escapeHtml(
    formatKitchenDateTime(order?.orderedAt || new Date().toISOString())
  );
  const orderIdLabel = escapeHtml(formatOrderId(order?.id));
  const dinersLabel = Math.max(1, Number(order?.comensales || 1));

  return `<!doctype html>
<html lang="es">
  <head>
    <meta charset="utf-8" />
    <title>Comanda ${escapeHtml(order?.mesa || "Mesa")}</title>
    <style>
      @page {
        size: ${paperConfig.pageWidth} auto;
        margin: ${paperConfig.pageMargin};
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #0f172a;
        font-family: "Inter", "Segoe UI", Arial, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      body {
        width: ${paperConfig.bodyWidth};
        margin: 0 auto;
        padding: 1mm 0 0;
      }

      .ticket {
        display: grid;
        gap: ${paperConfig.ticketGap};
        padding: ${paperConfig.shellPadding};
        border: 1px solid #dbe4f0;
        border-radius: ${paperConfig.shellRadius};
        background: #ffffff;
      }

      .ticket-brand {
        width: ${paperConfig.brandWidth};
        min-height: ${paperConfig.brandHeight};
        margin: 0 auto;
        border-radius: 999px;
        background: #f97316;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${paperConfig.brandFontSize};
        font-weight: 800;
        letter-spacing: 0.2px;
      }

      .ticket-header {
        display: grid;
        gap: 6px;
        text-align: center;
      }

      .ticket-kicker {
        font-size: ${paperConfig.kickerSize};
        font-weight: 700;
        letter-spacing: 1.4px;
        text-transform: uppercase;
        color: #475569;
      }

      .ticket-title {
        font-size: ${paperConfig.titleSize};
        font-weight: 800;
        line-height: 1.15;
      }

      .ticket-branch {
        font-size: ${paperConfig.branchSize};
        color: #475569;
      }

      .ticket-hero {
        display: grid;
        gap: 4px;
        text-align: center;
      }

      .ticket-headline {
        font-size: ${paperConfig.headlineSize};
        font-weight: 900;
        line-height: 1;
        letter-spacing: -0.4px;
      }

      .ticket-headline-subtitle {
        font-size: ${paperConfig.metaValueSize};
        color: #64748b;
      }

      .ticket-divider {
        border-top: 1px dashed #cbd5e1;
      }

      .ticket-meta-list {
        display: grid;
        gap: ${paperConfig.gridGap};
      }

      .ticket-meta-row {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: baseline;
        gap: 6px;
      }

      .ticket-meta-label {
        font-size: ${paperConfig.metaLabelSize};
        font-weight: 800;
        color: #0f172a;
      }

      .ticket-meta-value {
        font-size: ${paperConfig.metaValueSize};
        color: #0f172a;
        line-height: 1.35;
      }

      .ticket-section {
        display: grid;
        gap: ${paperConfig.gridGap};
      }

      .ticket-section-title {
        font-size: ${paperConfig.sectionTitleSize};
        font-weight: 800;
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      .ticket-items {
        display: grid;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .ticket-item {
        border-bottom: 1px dashed #cbd5e1;
        padding-bottom: 7px;
      }

      .ticket-item-main {
        display: grid;
        gap: 3px;
      }

      .ticket-item-title {
        font-size: ${paperConfig.itemTitleSize};
        font-weight: 800;
        line-height: 1.35;
      }

      .ticket-item-details {
        color: #64748b;
        font-size: ${paperConfig.detailSize};
        line-height: 1.45;
      }

      .ticket-note {
        border: 1px dashed #f59e0b;
        background: #fffbeb;
        border-radius: 12px;
        padding: 8px 9px;
        font-size: ${paperConfig.noteSize};
        line-height: 1.5;
        white-space: pre-wrap;
      }

      .ticket-footer {
        border-top: 1px dashed #cbd5e1;
        padding-top: 8px;
        text-align: center;
        color: #64748b;
        font-size: ${paperConfig.footerSize};
        line-height: 1.4;
      }
    </style>
  </head>
  <body>
    <main class="ticket">
      <div class="ticket-brand">MozzoApp</div>

      <header class="ticket-header">
        <div class="ticket-kicker">COMANDA DE COCINA</div>
        <div class="ticket-title">${headerTitle}</div>
        ${branchLabel}
      </header>

      <section class="ticket-hero">
        <div class="ticket-headline">${headline}</div>
        ${
          secondaryLabel
            ? `<div class="ticket-headline-subtitle">${escapeHtml(secondaryLabel)}</div>`
            : ""
        }
      </section>

      <div class="ticket-divider"></div>

      <section class="ticket-meta-list">
        <div class="ticket-meta-row">
          <span class="ticket-meta-label">Canal:</span>
          <span class="ticket-meta-value">${escapeHtml(channelLabel)}</span>
        </div>
        <div class="ticket-meta-row">
          <span class="ticket-meta-label">Hora:</span>
          <span class="ticket-meta-value">${orderedAtLabel}</span>
        </div>
        ${
          customerName
            ? `<div class="ticket-meta-row">
                <span class="ticket-meta-label">Cliente:</span>
                <span class="ticket-meta-value">${escapeHtml(customerName)}</span>
              </div>`
            : ""
        }
        <div class="ticket-meta-row">
          <span class="ticket-meta-label">Comensales:</span>
          <span class="ticket-meta-value">${dinersLabel}</span>
        </div>
        <div class="ticket-meta-row">
          <span class="ticket-meta-label">Pedido:</span>
          <span class="ticket-meta-value">${orderIdLabel}</span>
        </div>
      </section>

      <div class="ticket-divider"></div>

      <section class="ticket-section">
        <div class="ticket-section-title">Items</div>
        <ol class="ticket-items">
          ${items.map((item, index) => buildKitchenItemMarkup(item, index)).join("")}
        </ol>
      </section>

      ${
        notes
          ? `<section class="ticket-section">
              <div class="ticket-section-title">Notas</div>
              <div class="ticket-note">${escapeHtml(notes)}</div>
            </section>`
          : ""
      }

      <footer class="ticket-footer">
        Comanda generada desde MozzoApp para cocina
      </footer>
    </main>
  </body>
</html>`;
};

const buildCustomerPickupTicketHtml = ({
  restaurantName,
  branchName,
  order,
  paperWidth,
}) => {
  const headerTitle = escapeHtml(restaurantName || "MozzoApp");
  const paperConfig = resolveKitchenTicketPaperConfig(paperWidth);
  const branchLabel =
    branchName && branchName !== restaurantName
      ? `<div class="ticket-branch">${escapeHtml(branchName)}</div>`
      : "";
  const customerName = String(order?.customerName || "").trim();
  const preparationCode = formatPreparationNumber(order?.preparationNumber);
  const items = Array.isArray(order?.items) ? order.items : [];
  const paymentLabel = resolveCustomerTicketPaymentLabel(order);
  const totalAmount = Number(order?.pricing?.finalTotal ?? order?.totalAmount ?? 0);
  const serviceType = String(order?.serviceType || "").trim().toLowerCase();
  const serviceTypeLabel =
    serviceType === "takeaway"
      ? "Para llevar"
      : serviceType === "dine_in"
        ? "Comer aqui"
        : "Pedido";
  const orderedAtLabel = escapeHtml(
    formatKitchenDateTime(order?.orderedAt || new Date().toISOString())
  );

  return `<!doctype html>
<html lang="es">
  <head>
    <meta charset="utf-8" />
    <title>Ticket ${preparationCode || "Pedido"}</title>
    <style>
      @page {
        size: ${paperConfig.pageWidth} auto;
        margin: ${paperConfig.pageMargin};
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #0f172a;
        font-family: "Inter", "Segoe UI", Arial, sans-serif;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      body {
        width: ${paperConfig.bodyWidth};
        margin: 0 auto;
        padding: 1mm 0 0;
      }

      .ticket {
        display: grid;
        gap: ${paperConfig.ticketGap};
        padding: ${paperConfig.shellPadding};
        border: 1px solid #dbe4f0;
        border-radius: ${paperConfig.shellRadius};
        background: #ffffff;
      }

      .ticket-brand {
        width: ${paperConfig.brandWidth};
        min-height: ${paperConfig.brandHeight};
        margin: 0 auto;
        border-radius: 999px;
        background: #f97316;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${paperConfig.brandFontSize};
        font-weight: 800;
        letter-spacing: 0.2px;
      }

      .ticket-header {
        display: grid;
        gap: 6px;
        text-align: center;
      }

      .ticket-kicker {
        font-size: ${paperConfig.kickerSize};
        font-weight: 700;
        letter-spacing: 1.4px;
        text-transform: uppercase;
        color: #475569;
      }

      .ticket-title {
        font-size: ${paperConfig.titleSize};
        font-weight: 800;
        line-height: 1.2;
      }

      .ticket-branch {
        font-size: ${paperConfig.branchSize};
        color: #475569;
      }

      .ticket-code {
        margin-top: 2px;
        font-size: ${paperConfig.codeSize};
        font-weight: 900;
        line-height: 1;
        letter-spacing: -0.8px;
      }

      .ticket-divider {
        border-top: 1px dashed #cbd5e1;
      }

      .ticket-meta-list {
        display: grid;
        gap: ${paperConfig.gridGap};
      }

      .ticket-meta-row {
        display: grid;
        grid-template-columns: auto 1fr;
        align-items: baseline;
        gap: 6px;
      }

      .ticket-meta-label {
        font-size: ${paperConfig.metaLabelSize};
        font-weight: 800;
        color: #0f172a;
      }

      .ticket-meta-value {
        font-size: ${paperConfig.metaValueSize};
        color: #0f172a;
        line-height: 1.35;
      }

      .ticket-section {
        display: grid;
        gap: ${paperConfig.gridGap};
      }

      .ticket-section-title {
        font-size: ${paperConfig.sectionTitleSize};
        font-weight: 800;
        letter-spacing: 1px;
        text-transform: uppercase;
      }

      .ticket-items {
        display: grid;
        gap: ${paperConfig.gridGap};
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .ticket-item {
        display: grid;
        gap: 3px;
      }

      .ticket-item-title {
        font-size: ${paperConfig.itemTitleSize};
        font-weight: 500;
        line-height: 1.35;
        color: #0f172a;
      }

      .ticket-item-details {
        padding-left: 12px;
        font-size: ${paperConfig.detailSize};
        line-height: 1.4;
        color: #64748b;
      }

      .ticket-totals {
        display: grid;
        gap: 7px;
      }

      .ticket-total-row {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 6px;
        align-items: baseline;
      }

      .ticket-total-row--strong {
        grid-template-columns: auto 1fr auto;
      }

      .ticket-total-label {
        font-size: ${paperConfig.metaLabelSize};
        font-weight: 800;
        color: #0f172a;
      }

      .ticket-total-value {
        font-size: ${paperConfig.metaValueSize};
        color: #0f172a;
        line-height: 1.35;
      }

      .ticket-total-amount {
        font-size: ${paperConfig.totalSize};
        font-weight: 900;
        color: #0f172a;
      }

      .ticket-footer {
        border-top: 1px dashed #cbd5e1;
        padding-top: 8px;
        text-align: center;
        color: #475569;
        font-size: ${paperConfig.footerSize};
        line-height: 1.45;
      }
    </style>
  </head>
  <body>
    <main class="ticket">
      <div class="ticket-brand">MozzoApp</div>

      <header class="ticket-header">
        <div class="ticket-kicker">Ticket de retiro</div>
        <div class="ticket-title">${headerTitle}</div>
        ${branchLabel}
        ${preparationCode ? `<div class="ticket-code">#${escapeHtml(preparationCode)}</div>` : ""}
      </header>

      <div class="ticket-divider"></div>

      <section class="ticket-meta-list">
        ${
          customerName
            ? `<div class="ticket-meta-row">
                <span class="ticket-meta-label">Nombre:</span>
                <span class="ticket-meta-value">${escapeHtml(customerName)}</span>
              </div>`
            : ""
        }
        <div class="ticket-meta-row">
          <span class="ticket-meta-label">Modalidad:</span>
          <span class="ticket-meta-value">${escapeHtml(serviceTypeLabel)}</span>
        </div>
        <div class="ticket-meta-row">
          <span class="ticket-meta-label">Hora:</span>
          <span class="ticket-meta-value">${orderedAtLabel}</span>
        </div>
      </section>

      ${
        items.length
          ? `<div class="ticket-divider"></div>
             <section class="ticket-section">
              <div class="ticket-section-title">Items</div>
              <ol class="ticket-items">
                ${items
                  .map((item, index) => {
                    const quantity = Math.max(1, Number(item?.quantity || 1));
                    const productName = resolveTicketItemName(item);
                    return `<li class="ticket-item">
                      <div class="ticket-item-title">${index + 1}. ${quantity} x ${productName}</div>
                      ${buildTicketItemNotesMarkup(item)}
                    </li>`;
                  })
                  .join("")}
              </ol>
             </section>`
          : ""
      }

      ${
        paymentLabel || totalAmount > 0
          ? `<div class="ticket-divider"></div>
             <section class="ticket-totals">
               ${
                 paymentLabel
                   ? `<div class="ticket-total-row">
                       <span class="ticket-total-label">Pago:</span>
                       <span class="ticket-total-value">${escapeHtml(paymentLabel)}</span>
                     </div>`
                   : ""
               }
               ${
                 totalAmount > 0
                   ? `<div class="ticket-total-row ticket-total-row--strong">
                       <span class="ticket-total-label">Total:</span>
                       <span></span>
                       <span class="ticket-total-amount">${escapeHtml(
                         new Intl.NumberFormat("es-AR", {
                           style: "currency",
                           currency: "ARS",
                         }).format(totalAmount)
                       )}</span>
                     </div>`
                   : ""
               }
             </section>`
          : ""
      }

      <footer class="ticket-footer">
        Conserva este ticket para el llamado por pantalla.<br />
        Te entregaremos el pedido por el codigo #${escapeHtml(preparationCode || "---")}.
      </footer>
    </main>
  </body>
</html>`;
};

const RUNTIME_PRINT_STAGE_CLASS = "mozzoapp-runtime-print-stage";
const RUNTIME_PRINT_ROOT_CLASS = "mozzoapp-runtime-print-root";
const RUNTIME_PRINT_ACTIVE_ATTRIBUTE = "data-mozzoapp-runtime-print";
const RUNTIME_PRINT_TIMEOUT_MS = 45000;

const extractPrintDocumentParts = (html) => {
  const styleMatches = Array.from(
    String(html || "").matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi)
  );
  const styles = styleMatches.map((match) => String(match?.[1] || "")).join("\n");
  const bodyMatch = String(html || "").match(/<body[^>]*>([\s\S]*?)<\/body>/i);

  return {
    styles,
    bodyMarkup: bodyMatch ? String(bodyMatch[1] || "") : String(html || ""),
  };
};

const scopeRuntimePrintStyles = (styles) =>
  String(styles || "")
    .replace(/html\s*,\s*body/gi, `.${RUNTIME_PRINT_ROOT_CLASS}`)
    .replace(/\bbody\s*\{/gi, `.${RUNTIME_PRINT_ROOT_CLASS} {`);

const buildRuntimePrintStageMarkup = (html) => {
  const { styles, bodyMarkup } = extractPrintDocumentParts(html);
  const scopedStyles = scopeRuntimePrintStyles(styles);

  return `
    <style>
      body[${RUNTIME_PRINT_ACTIVE_ATTRIBUTE}="true"] {
        background: #ffffff !important;
      }

      body[${RUNTIME_PRINT_ACTIVE_ATTRIBUTE}="true"] > *:not(.${RUNTIME_PRINT_STAGE_CLASS}) {
        display: none !important;
      }

      .${RUNTIME_PRINT_STAGE_CLASS} {
        display: none;
      }

      body[${RUNTIME_PRINT_ACTIVE_ATTRIBUTE}="true"] > .${RUNTIME_PRINT_STAGE_CLASS} {
        position: fixed;
        inset: 0;
        z-index: 2147483647;
        display: block;
        overflow: auto;
        background: #ffffff;
        padding: 0;
      }

      @media print {
        body[${RUNTIME_PRINT_ACTIVE_ATTRIBUTE}="true"] {
          margin: 0 !important;
          padding: 0 !important;
          background: #ffffff !important;
        }

        body[${RUNTIME_PRINT_ACTIVE_ATTRIBUTE}="true"] > *:not(.${RUNTIME_PRINT_STAGE_CLASS}) {
          display: none !important;
        }

        body[${RUNTIME_PRINT_ACTIVE_ATTRIBUTE}="true"] > .${RUNTIME_PRINT_STAGE_CLASS} {
          position: static !important;
          inset: auto !important;
          overflow: visible !important;
          display: block !important;
          background: #ffffff !important;
        }
      }

      ${scopedStyles}
    </style>
    <div class="${RUNTIME_PRINT_ROOT_CLASS}">
      ${bodyMarkup}
    </div>
  `;
};

const printTicketInCurrentDocument = ({
  title,
  html,
  errorMessage,
  cleanupDelayMs = 0,
}) =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(new Error("La impresion solo esta disponible en el navegador."));
      return;
    }

    const stage = document.createElement("div");
    stage.className = RUNTIME_PRINT_STAGE_CLASS;
    stage.setAttribute("aria-hidden", "true");
    stage.innerHTML = buildRuntimePrintStageMarkup(html);

    let settled = false;
    let hardTimeoutId = 0;
    let appWentToBackground =
      document.visibilityState === "hidden" ||
      (typeof document.hasFocus === "function" && !document.hasFocus());
    const previousTitle = document.title;

    const cleanup = () => {
      if (hardTimeoutId) {
        window.clearTimeout(hardTimeoutId);
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange, true);
      window.removeEventListener("focus", handleFocus, true);
      window.removeEventListener("blur", handleBlur, true);
      window.removeEventListener("afterprint", handleAfterPrint, true);
      document.body.removeAttribute(RUNTIME_PRINT_ACTIVE_ATTRIBUTE);
      stage.remove();

      if (previousTitle) {
        document.title = previousTitle;
      }
    };

    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      window.setTimeout(() => {
        cleanup();
        resolve();
      }, Math.max(0, Number(cleanupDelayMs || 0)));
    };

    const canFinishSafely = () => {
      const isVisible = document.visibilityState !== "hidden";
      const hasFocus =
        typeof document.hasFocus === "function" ? document.hasFocus() : true;

      return appWentToBackground ? isVisible && hasFocus : true;
    };

    function handleAfterPrint() {
      if (canFinishSafely()) {
        finish();
      }
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        appWentToBackground = true;
        return;
      }

      if (canFinishSafely()) {
        finish();
      }
    }

    function handleFocus() {
      if (canFinishSafely()) {
        finish();
      }
    }

    function handleBlur() {
      appWentToBackground = true;
    }

    try {
      if (title) {
        document.title = title;
      }

      document.body.appendChild(stage);
      document.body.setAttribute(RUNTIME_PRINT_ACTIVE_ATTRIBUTE, "true");
      document.addEventListener("visibilitychange", handleVisibilityChange, true);
      window.addEventListener("focus", handleFocus, true);
      window.addEventListener("blur", handleBlur, true);
      window.addEventListener("afterprint", handleAfterPrint, true);

      hardTimeoutId = window.setTimeout(() => {
        finish();
      }, RUNTIME_PRINT_TIMEOUT_MS);

      window.setTimeout(() => {
        try {
          window.focus();
          window.print();
        } catch (error) {
          cleanup();
          reject(
            error instanceof Error
              ? error
              : new Error(errorMessage)
          );
        }
      }, 120);
    } catch (error) {
      cleanup();
      reject(
        error instanceof Error
          ? error
          : new Error(errorMessage)
      );
    }
  });

const printTicketDocument = ({ title, html, errorMessage }) =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      reject(new Error("La impresion solo esta disponible en el navegador."));
      return;
    }

    const iframe = document.createElement("iframe");
    iframe.setAttribute("title", title);
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.top = "-10000px";
    iframe.style.left = "-10000px";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    iframe.style.opacity = "0";
    iframe.style.border = "0";
    iframe.style.pointerEvents = "none";

    let settled = false;
    let hardTimeoutId = 0;
    let focusListenerAttached = false;

    const cleanup = () => {
      if (hardTimeoutId) {
        window.clearTimeout(hardTimeoutId);
      }

      if (focusListenerAttached) {
        window.removeEventListener("focus", handleWindowFocus, true);
      }

      window.setTimeout(() => {
        iframe.remove();
      }, 1500);
    };

    const settle = (callback) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      callback();
    };

    const handleWindowFocus = () => {
      window.setTimeout(() => {
        settle(() => resolve());
      }, 250);
    };

    iframe.onload = () => {
      // document.close() can emit another load; initialize the ticket only once.
      iframe.onload = null;
      const printWindow = iframe.contentWindow;
      const printDocument = iframe.contentDocument;

      if (!printWindow || !printDocument) {
        cleanup();
        reject(new Error(errorMessage));
        return;
      }

      try {
        printDocument.open();
        printDocument.write(html);
        printDocument.close();
      } catch (error) {
        cleanup();
        reject(
          error instanceof Error
            ? error
            : new Error(errorMessage)
        );
        return;
      }

      const startPrint = () => {
        if (settled) {
          return;
        }

        try {
          const afterPrintHandler = () => settle(() => resolve());
          if (typeof printWindow.addEventListener === "function") {
            printWindow.addEventListener("afterprint", afterPrintHandler, { once: true });
          } else {
            printWindow.onafterprint = afterPrintHandler;
          }

          if (!focusListenerAttached) {
            focusListenerAttached = true;
            window.addEventListener("focus", handleWindowFocus, true);
          }

          hardTimeoutId = window.setTimeout(() => {
            settle(() => resolve());
          }, 30000);

          printWindow.focus();
          printWindow.print();
        } catch (error) {
          settle(() =>
            reject(
              error instanceof Error
                ? error
                : new Error(errorMessage)
            )
          );
        }
      };

      window.setTimeout(startPrint, 220);
    };

    iframe.src = "about:blank";
    document.body.appendChild(iframe);
  });

export const printKitchenTicket = ({
  restaurantName,
  branchName,
  order,
  paperWidth,
  preferCurrentDocumentPrint = false,
  cleanupDelayMs = 0,
}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.LOCAL_PRINTING);

  if (!order) {
    return Promise.reject(new Error("No encontramos el pedido para imprimir."));
  }

  if (!orderRequiresKitchen(order)) {
    return Promise.resolve({ skipped: true });
  }

  const printPayload = {
    title: `Comanda ${String(order?.mesa || "Mesa")}`,
    html: buildKitchenTicketHtml({
      restaurantName,
      branchName,
      order,
      paperWidth,
    }),
    errorMessage: "No pudimos lanzar la impresion de la comanda.",
  };

  if (preferCurrentDocumentPrint) {
    return printTicketInCurrentDocument({
      ...printPayload,
      cleanupDelayMs,
    });
  }

  return printTicketDocument(printPayload);
};

export const printCustomerPickupTicket = ({
  restaurantName,
  branchName,
  order,
  paperWidth,
  preferCurrentDocumentPrint = false,
  cleanupDelayMs = 0,
}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.LOCAL_PRINTING);

  if (!order) {
    return Promise.reject(new Error("No encontramos el pedido para imprimir."));
  }

  const preparationCode = formatPreparationNumber(order?.preparationNumber) || "Pedido";

  const printPayload = {
    title: `Ticket ${preparationCode}`,
    html: buildCustomerPickupTicketHtml({
      restaurantName,
      branchName,
      order,
      paperWidth,
    }),
    errorMessage: "No pudimos lanzar la impresion del ticket del cliente.",
  };

  if (preferCurrentDocumentPrint) {
    return printTicketInCurrentDocument({
      ...printPayload,
      cleanupDelayMs,
    });
  }

  return printTicketDocument(printPayload);
};

export const printCustomerBill = (options) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.LOCAL_PRINTING);
  if (!options?.order) return Promise.reject(new Error("No encontramos la cuenta para imprimir."));
  return printTicketDocument({
    title: `Cuenta ${options.order.mesa || "Cliente"}`,
    html: buildCustomerBillHtml(options),
    errorMessage: "No pudimos abrir la impresion de la cuenta.",
  });
};

export default printKitchenTicket;
